import { ReactNode, useState } from 'react';

import { Divider, Stack, Typography } from '@mui/material';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

import { PeriodProps } from './_types';
import { PeriodComparisonPickerBody } from './PeriodComparisonPickerBody';
import { PeriodComparisonPickerFooter } from './PeriodComparisonPickerFooter';
import { PeriodPresetsList, ValidatedPreset } from './PeriodPresetsList';
import { tokens } from '../../../../theme/generated/tokens';
import { SxProps } from '../../../../types';
import { CalendarBlank } from '../../../icons';
import {
  DatePickerInput,
  DatePickerInputProps,
} from '../../components/DatePickerInput';
import { DatePickerPopper } from '../../components/DatePickerPopper';
import { defaultDateFormat, defaultDateTimeFormat } from '../../constants';
import DatePickerProvider from '../../DatePickerProvider';
import {
  DatePeriod,
  DatePeriodInTimestamp,
  InfiniteDayPickerRef,
  MarkedDate,
  PeriodPreset,
} from '../../types';
import {
  utcToDate,
  convertRange,
  rangeToUtc,
  dayjsToTZDate,
  periodToString,
} from '../../utils';

dayjs.extend(utc);
dayjs.extend(timezone);

export type PeriodComparisonPickerProps = {
  initialStartDates?: { periodA?: string; periodB?: string };
  onChange?: (
    dates: { periodA: DatePeriodInTimestamp; periodB: DatePeriodInTimestamp },
    timezone: string,
    preset: number
  ) => void;
  initialPreset?: number;
  format?: string;
  triggerInputProps?: DatePickerInputProps['inputProps'];
  periodPresets?: PeriodPreset[] | ((timezone: string) => PeriodPreset[]);
  hideRangeInputs?: boolean;
  allowTimeInput?: boolean;
  hidePresets?: boolean;
  maxNumberOfDays?: number | ((timezone: string) => number);
  hideTimezonePicker?: boolean;
  initialTimezone?: string;
  markedDates?: MarkedDate[];
  presetValidator?: () => ValidatedPreset[];
  hint?:
    | ReactNode
    | ((args: {
        periodA: DatePeriodInTimestamp;
        periodB: DatePeriodInTimestamp;
        timezone: string;
        pickerARef: InfiniteDayPickerRef;
        pickerBRef: InfiniteDayPickerRef;
      }) => ReactNode);
  periodAProps?: PeriodProps;
  periodBProps?: PeriodProps;
  inputValue?: (
    periodA: DatePeriodInTimestamp,
    periodB: DatePeriodInTimestamp,
    timezone: string
  ) => string;
  sx?: SxProps;
};

const buildInitialRange = (
  initialStartDate: string | undefined,
  activePeriod: number | undefined,
  timezone: string
) => {
  if (!activePeriod || !initialStartDate) {
    return {
      from: initialStartDate
        ? utcToDate(initialStartDate, timezone)
        : undefined,
      to: undefined,
    };
  }

  return {
    from: utcToDate(initialStartDate, timezone),
    to: dayjsToTZDate(
      dayjs.utc(initialStartDate).add(activePeriod - 1 * 24 * 60, 'minutes'),
      timezone
    ),
  };
};

export const PeriodComparisonPicker = ({
  initialStartDates,
  initialPreset,
  onChange,
  periodPresets = [],
  allowTimeInput = false,
  format: _format = allowTimeInput ? defaultDateTimeFormat : defaultDateFormat,
  triggerInputProps,
  hint,
  hidePresets = false,
  maxNumberOfDays,
  hideRangeInputs = false,
  hideTimezonePicker = false,
  markedDates = [],
  initialTimezone = dayjs.tz.guess(),
  periodAProps,
  periodBProps,
  inputValue,
  sx,
}: PeriodComparisonPickerProps) => {
  const [pickerARef, setPickerARef] = useState<InfiniteDayPickerRef>(null);
  const [pickerBRef, setPickerBRef] = useState<InfiniteDayPickerRef>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLInputElement | null>(null);
  const [selectedA, setSelectedA] = useState<DatePeriod>(() =>
    buildInitialRange(
      initialStartDates?.periodA,
      initialPreset,
      initialTimezone
    )
  );
  const [selectedB, setSelectedB] = useState<DatePeriod>(() =>
    buildInitialRange(
      initialStartDates?.periodB,
      initialPreset,
      initialTimezone
    )
  );
  const [timezone, setTimezone] = useState<string>(initialTimezone);

  const [selectedActivePeriod, setSelectedActivePeriod] = useState<
    number | undefined
  >(initialPreset);

  const handleOpen = (event: React.MouseEvent<HTMLInputElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const onClose = () => {
    setTimezone(initialTimezone);
    setSelectedA(
      buildInitialRange(
        initialStartDates?.periodA,
        initialPreset,
        initialTimezone
      )
    );
    setSelectedB(
      buildInitialRange(
        initialStartDates?.periodB,
        initialPreset,
        initialTimezone
      )
    );
    setSelectedActivePeriod(initialPreset);
    handleClose();
  };

  const handleTimezoneChange = (newTimezone: string) => {
    const convertedPeriodA = convertRange(selectedA, timezone, newTimezone);
    setSelectedA(convertedPeriodA);

    const convertedPeriodB = convertRange(selectedB, timezone, newTimezone);
    setSelectedB(convertedPeriodB);

    setTimezone(newTimezone);
  };

  const handleApply = () => {
    if (selectedA?.from && selectedB?.from && selectedActivePeriod) {
      onChange?.(
        {
          periodA: rangeToUtc(selectedA),
          periodB: rangeToUtc(selectedB),
        },
        timezone,
        selectedActivePeriod
      );

      handleClose();
    }
  };

  const formattedInputValue = (
    selectedA?: DatePeriod,
    selectedB?: DatePeriod
  ) => {
    if (selectedA?.from && selectedB?.from) {
      return `${dayjs.tz(selectedA.from, timezone).format(_format)} vs ${dayjs
        .tz(selectedB.from, timezone)
        .format(_format)}`;
    }
    return '';
  };

  const inputValueFormatter = inputValue
    ? inputValue.bind(
        null,
        rangeToUtc(selectedA),
        rangeToUtc(selectedB),
        timezone
      )
    : formattedInputValue.bind(null, selectedA, selectedB);

  const clearRanges = () => {
    setSelectedA({ from: undefined, to: undefined });
    setSelectedB({ from: undefined, to: undefined });
  };

  return (
    <DatePickerProvider
      mode="range"
      format={_format}
      inputValue={inputValueFormatter}
      popoverAnchor={anchorEl}
      setPopoverAnchor={setAnchorEl}
      handleOpen={handleOpen}
      handleClose={onClose}
      handleApply={handleApply}
      periodPresets={
        typeof periodPresets === 'function'
          ? periodPresets(timezone)
          : periodPresets
      }
      activePeriod={selectedActivePeriod}
      setActivePeriod={setSelectedActivePeriod}
      allowTimeInput={allowTimeInput}
      maxNumberOfDays={
        typeof maxNumberOfDays === 'function'
          ? maxNumberOfDays(timezone)
          : maxNumberOfDays
      }
      hideTimezonePicker={hideTimezonePicker}
      timezone={timezone}
      setTimezone={handleTimezoneChange}
      markedDates={markedDates}
      hidePresets={hidePresets}
      clearRanges={clearRanges}
    >
      <DatePickerInput
        sx={sx}
        inputProps={{
          startAdornment: selectedActivePeriod ? (
            <Stack direction="row" alignItems="center" gap="12px" height="100%">
              <Typography
                variant="P14R"
                sx={{ color: tokens.theme.fg.structural.secondary }}
                noWrap
                data-testid="active-period"
              >
                {periodToString(selectedActivePeriod)}
              </Typography>
              <Divider orientation="vertical" />
              <CalendarBlank size="20px" />
            </Stack>
          ) : (
            <CalendarBlank size="20px" />
          ),
          ...triggerInputProps,
        }}
        popperContent={
          <DatePickerPopper
            aside={
              hidePresets ? null : (
                <Stack
                  minWidth={178}
                  padding="8px 16px"
                  borderRight={`1px solid ${tokens.theme.border.structural.neutral}`}
                >
                  <PeriodPresetsList />
                </Stack>
              )
            }
            footer={
              <PeriodComparisonPickerFooter
                isMini={hidePresets}
                hint={
                  typeof hint === 'function'
                    ? hint({
                        periodA: rangeToUtc(selectedA),
                        periodB: rangeToUtc(selectedB),
                        timezone,
                        pickerARef,
                        pickerBRef,
                      })
                    : hint
                }
              />
            }
          >
            <Stack direction="row" mt={16}>
              <PeriodComparisonPickerBody
                label="Period A"
                variant="main"
                hideRangeInputs={hideRangeInputs}
                initialRange={initialStartDates?.periodA}
                periodProps={{
                  selected: selectedA,
                  setSelected: setSelectedA,
                  pickerRef: pickerARef,
                  setPickerRef: setPickerARef,
                  ...periodAProps,
                }}
                otherRange={selectedB}
              />
              <Divider
                sx={{
                  mt: -16,
                  borderColor: tokens.theme.border.structural.neutral,
                }}
                orientation="vertical"
                flexItem
              />
              <PeriodComparisonPickerBody
                label="Period B"
                variant="secondary"
                hideRangeInputs={hideRangeInputs}
                initialRange={initialStartDates?.periodB}
                periodProps={{
                  selected: selectedB,
                  setSelected: setSelectedB,
                  pickerRef: pickerBRef,
                  setPickerRef: setPickerBRef,
                  ...periodBProps,
                }}
                otherRange={selectedA}
              />
            </Stack>
          </DatePickerPopper>
        }
      />
    </DatePickerProvider>
  );
};
